import classNames from 'classnames';
import React, { PureComponent, RefObject } from 'react';

import { append } from '../../../utils';

import { IconDeprecated } from '../IconDeprecated';
import { InputComponent, PlainInput } from '../../Input';

import { CosmosComponent, OptionProps } from '../../../types';

import styles from './DropdownDeprecated.module.css';

export interface DropdownDeprecatedProps extends CosmosComponent {
  options?: OptionProps[];
  optionsWidth?: string;
  initialValue?: OptionProps;
  isSettingState?: boolean;
  onOptionSelect: (selected: OptionProps) => void;
}

type DropdownComponent = DropdownDeprecatedProps & InputComponent;

interface DropdownStateProps {
  open: boolean;
  selected?: OptionProps;
}

const defaultSelected: OptionProps = {
  id: '',
  value: '',
};

export class DropdownDeprecated extends PureComponent<
  DropdownComponent,
  DropdownStateProps
> {
  public static defaultProps: Partial<DropdownComponent> = {
    isInverted: false,
    readOnly: true,
    isSettingState: true,
    options: [],
  };

  public state = {
    open: false,
    selected: this.props.initialValue || defaultSelected,
  };

  private dropdown: RefObject<HTMLDivElement> = React.createRef();

  public componentDidMount() {
    document.addEventListener('click', this.handleOutsideClick);
  }

  public componentWillUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  }

  public render() {
    const { selected, open } = this.state;
    const {
      icon,
      options,
      optionsWidth,
      isInverted,
      className,
      value,
      readOnly,
      onInputChange,
      name = 'dropdown',
      placeholder,
      testId,
    } = this.props;

    // Use internally controlled component only if value prop is not provided
    const inputValue = value === undefined ? selected.value : value;

    return (
      <div
        ref={this.dropdown}
        className={classNames(className, styles.Dropdown, {
          [styles.inverted]: isInverted,
        })}
        data-test-id={testId}
      >
        <div className={styles.Label} onClick={this.toggleOptions}>
          {icon && (
            <IconDeprecated
              isInverted={isInverted}
              className={styles.Icon}
              name={icon}
            />
          )}
          <PlainInput
            isInverted={isInverted}
            testId={append(testId, 'input')}
            readOnly={readOnly}
            value={inputValue}
            title={`${selected.value}`}
            onInputChange={onInputChange}
            name={name}
            placeholder={placeholder}
          />
          <IconDeprecated
            testId={append(testId, 'icon')}
            isInverted={isInverted}
            className={styles.Icon}
            name="chevron"
          />
        </div>
        {options && !!options.length && open && (
          <div
            className={styles.Options}
            style={optionsWidth ? { width: optionsWidth } : {}}
          >
            <div
              role="listbox"
              aria-label={`${name}-listbox`}
              className={styles.Wrapper}
            >
              {this.getOptions(options, name, testId)}
            </div>
          </div>
        )}
      </div>
    );
  }

  private getOptions = (
    options: OptionProps[],
    name: string,
    testId?: string,
  ) =>
    options.map((option, i) => (
      <div
        role="option"
        aria-label={`${name}-option-${i}`}
        key={`${option.id}${i}`}
        className={classNames(styles.Option, {
          [styles.selected]: this.state.selected.id === option.id,
        })}
        onClick={() => this.selectOption(option)}
        data-test-id={append(testId, `option-${i + 1}`)}
      >
        <span className={styles.Text} title={`${option.value}`}>
          {option.value}
        </span>
      </div>
    ));

  private toggleOptions = () =>
    this.setState((state: DropdownStateProps) => ({
      open: !state.open,
    }));

  private selectOption = (option: OptionProps) => {
    const { isSettingState, onOptionSelect } = this.props;

    if (isSettingState) {
      this.setState(() => ({
        selected: option,
        open: false,
      }));
    }
    if (onOptionSelect) {
      onOptionSelect(option);
    }
  };

  private handleOutsideClick = (event) => {
    const { open } = this.state;
    const { target } = event;
    const dropdownNode = this.dropdown ? this.dropdown.current : null;

    if (open && dropdownNode && !dropdownNode.contains(target)) {
      this.setState(() => ({
        open: false,
      }));
    }
  };
}
